import { Platform, BatchItem, BatchJob } from '@blisspointmedia/bpm-types/dist/CommerceScraper';
import {
  awaitJSON,
  CommerceOpsLambdaFetch,
  SEQUENTIAL_VISIBILITY_WAIT_TIMEOUT_MS
} from '../../utils/fetch-utils';
import { equalsIgnoreCase } from '../../utils/text-utils';

export type { Platform, BatchItem, BatchJob };

// ── Platform & Tab Types ──────────────────────────────────────────────

export const AMAZON: Platform = 'amazon';
export const WALMART: Platform = 'walmart';

export const AmazonTab = {
  PRODUCT: 'product',
  SEARCH: 'search',
  REVIEWS: 'reviews',
  SCORECARD: 'scorecard'
} as const;
export type AmazonTab = (typeof AmazonTab)[keyof typeof AmazonTab];

export const WalmartTab = {
  PRODUCT: 'product',
  SEARCH: 'search',
  CATEGORY: 'category',
  REVIEWS: 'reviews'
} as const;
export type WalmartTab = (typeof WalmartTab)[keyof typeof WalmartTab];

export type ScraperTab = AmazonTab | WalmartTab;

export const AMAZON_NAVS = [
  { key: AmazonTab.PRODUCT, label: 'Product' },
  { key: AmazonTab.SEARCH, label: 'Search' },
  { key: AmazonTab.REVIEWS, label: 'Reviews' },
  { key: AmazonTab.SCORECARD, label: 'Scorecard' }
];

export const WALMART_NAVS = [
  { key: WalmartTab.PRODUCT, label: 'Product' },
  { key: WalmartTab.SEARCH, label: 'Search' },
  { key: WalmartTab.CATEGORY, label: 'Category' },
  { key: WalmartTab.REVIEWS, label: 'Reviews' }
];

// ── TLD Options ───────────────────────────────────────────────────────

export interface TldOption {
  value: string;
  label: string;
}

export const AMAZON_TLDS: TldOption[] = [
  { value: 'com', label: 'US (amazon.com)' },
  { value: 'co.uk', label: 'UK (amazon.co.uk)' },
  { value: 'ca', label: 'CA (amazon.ca)' },
  { value: 'de', label: 'DE (amazon.de)' },
  { value: 'es', label: 'ES (amazon.es)' },
  { value: 'fr', label: 'FR (amazon.fr)' },
  { value: 'it', label: 'IT (amazon.it)' },
  { value: 'co.jp', label: 'JP (amazon.co.jp)' },
  { value: 'in', label: 'IN (amazon.in)' },
  { value: 'com.au', label: 'AU (amazon.com.au)' },
  { value: 'com.br', label: 'BR (amazon.com.br)' },
  { value: 'com.mx', label: 'MX (amazon.com.mx)' },
  { value: 'nl', label: 'NL (amazon.nl)' },
  { value: 'sg', label: 'SG (amazon.sg)' },
  { value: 'sa', label: 'SA (amazon.sa)' },
  { value: 'com.tr', label: 'TR (amazon.com.tr)' },
  { value: 'ae', label: 'AE (amazon.ae)' },
  { value: 'pl', label: 'PL (amazon.pl)' }
];

export const WALMART_TLDS: TldOption[] = [
  { value: 'com', label: 'US (walmart.com)' },
  { value: 'ca', label: 'CA (walmart.ca)' }
];

// ── Sort Options ──────────────────────────────────────────────────────

export const AMAZON_SEARCH_SORT_OPTIONS = [
  { value: '', label: 'Relevance' },
  { value: 'review-rank', label: 'Avg. Customer Review' },
  { value: 'date-desc-rank', label: 'Newest Arrivals' },
  { value: 'price-asc-rank', label: 'Price: Low to High' },
  { value: 'price-desc-rank', label: 'Price: High to Low' }
];

export const WALMART_REVIEW_SORT_OPTIONS = [
  { value: 'relevancy', label: 'Most Relevant' },
  { value: 'submission-desc', label: 'Newest' },
  { value: 'submission-asc', label: 'Oldest' },
  { value: 'rating-desc', label: 'Highest Rating' },
  { value: 'rating-asc', label: 'Lowest Rating' },
  { value: 'helpfulness', label: 'Most Helpful' }
];

// ── API Functions ─────────────────────────────────────────────────────

const SCRAPER_PREFIX = '/scraper';

export const ScrapeStatus = {
  ERROR: 'error',
  EMPTY: 'empty',
  PARTIAL: 'partial',
  OK: 'ok',
  REDIRECTED: 'redirected'
} as const;
export type ScrapeStatusType = (typeof ScrapeStatus)[keyof typeof ScrapeStatus];

// ScraperAPI (behind the commerceops Lambda) can return an HTTP 200 with a stub/empty payload when
// the upstream Amazon scrape was throttled or blocked, rather than a clean error status. Neither
// BpmLambdaFetch's network-failure retry nor fetchWithRetry sees that as a failure on its own, so
// fetchAmazonProduct below checks the payload itself and throws — that's what lets the batch lookup's
// fetchWithRetry (and its failure banner) actually catch a throttled scrape. Shared with
// flattenAmazonProduct, which uses the same signal to label CSV rows that got through.
//
// 'redirected' (DSH-32549, 2026-08-28): confirmed live — some ASINs (personalization/
// subscription-quiz SKUs) have no independent Amazon listing of their own; Amazon deterministically
// serves a real sibling product's page instead. The backend (rainforestApiService.ts /
// rainforestCollectionsService.ts) detects this and sets redirect_asin rather than discarding the
// data, so the row's other fields belong to the redirected-to product, not a real scrape of the
// requested ASIN itself.
//
// DSH-32549 review feedback (W1): only downgrade to 'redirected' when the base classification
// would otherwise have been 'ok' — a data-free redirect (no name/pricing/etc. of its own) must
// still classify 'empty'/'partial' so fetchAmazonProduct throws on it, the same as any other
// scrape with nothing to show, rather than rendering a blank product as if it were a success.
// DSH-32549 review feedback (follow-up on I1): scorecard-import-utils.ts's runAsinBatchScore had
// its own bare `if (raw.redirect_asin)` truthy check on this exact field, so a whitespace-only or
// self-referential redirect_asin — cases this function explicitly treats as NOT a redirect —
// wrongly threw there and marked a legitimately-scorable ASIN 'failed'. Exported so both call
// sites agree on what counts as a genuine redirect instead of drifting again.
export const isGenuineRedirect = (p: Record<string, unknown>): boolean => {
  const redirectAsin = String(p.redirect_asin ?? '').trim();
  const ownAsin = String(p.asin ?? '').trim();
  return redirectAsin.length > 0 && !equalsIgnoreCase(redirectAsin, ownAsin);
};

export const classifyAmazonScrapeStatus = (p: Record<string, unknown>): ScrapeStatusType => {
  if (p.error || p.scrape_error || p.status === 'error' || p.status === 'failed') {
    return ScrapeStatus.ERROR;
  }
  const productInfo = p.product_information as Record<string, unknown> | undefined;
  const hasName = Boolean(String(p.name ?? '').trim());
  const hasAsin = Boolean(String(p.asin ?? productInfo?.asin ?? '').trim());
  const baseStatus = (():
    typeof ScrapeStatus.EMPTY | typeof ScrapeStatus.PARTIAL | typeof ScrapeStatus.OK => {
    if (!hasName && !hasAsin) return ScrapeStatus.EMPTY;
    const coreSignals = [
      p.pricing,
      p.average_rating,
      p.total_ratings,
      p.availability_status,
      productInfo && Object.keys(productInfo).length ? productInfo : null,
      Array.isArray(p.feature_bullets) && p.feature_bullets.length ? p.feature_bullets : null
    ];
    const populated = coreSignals.filter(v => v != null && v !== '').length;
    return populated === 0 ? ScrapeStatus.PARTIAL : ScrapeStatus.OK;
  })();

  return isGenuineRedirect(p) && baseStatus === ScrapeStatus.OK
    ? ScrapeStatus.REDIRECTED
    : baseStatus;
};

// DSH-32549 review feedback (W2): single source of truth for "is this batch job running against
// Amazon-product-shaped payloads" — classifyAmazonScrapeStatus (and getBatchBannerCounts below)
// only makes sense for that shape. Required params (I5): the only real caller always has both
// platform and tab in scope, so an accidentally-omitted one is a compile error rather than a
// silently-false check that zeros every count.
export const isAmazonProductBatch = (platform: Platform, tab: ScraperTab): boolean =>
  platform === AMAZON && tab === AmazonTab.PRODUCT;

export interface BatchBannerCounts {
  // Genuinely missing from the results/export — no row exists for these at all.
  excludedCount: number;
  // Present in the results/export, but that row's own scrape came back error/empty/partial.
  includedButBadCount: number;
  // Present in the results/export, redirected to a sibling product's listing.
  redirectedCount: number;
}

// DSH-32549 review feedback (I2/I3/I4): extracted out of ScraperResults.tsx so this logic is
// testable outside a component (CLAUDE.md: don't write component-rendering tests in vitest) and
// computed in a single pass rather than three separate filters each re-running the classifier per
// item. includedButBadCount and redirectedCount both require item.status === 'done' — a non-'done'
// item never has a meaningful result to classify (executeAmazonProductCollectionBatch only ever
// sets `result` alongside `status: 'done'`) — so the three buckets are provably disjoint from this
// code rather than only by that producer's invariant.
export const getBatchBannerCounts = (
  job: BatchJob,
  platform: Platform,
  tab: ScraperTab
): BatchBannerCounts => {
  const isAmazonProduct = isAmazonProductBatch(platform, tab);
  return job.items.reduce<BatchBannerCounts>(
    (counts, item) => {
      if (item.status === 'failed') {
        return { ...counts, excludedCount: counts.excludedCount + 1 };
      }
      if (!isAmazonProduct || item.status !== 'done' || !item.result) {
        return counts;
      }
      const status = classifyAmazonScrapeStatus(item.result);
      if (status === ScrapeStatus.REDIRECTED) {
        return { ...counts, redirectedCount: counts.redirectedCount + 1 };
      }
      if (
        status === ScrapeStatus.ERROR ||
        status === ScrapeStatus.EMPTY ||
        status === ScrapeStatus.PARTIAL
      ) {
        return { ...counts, includedButBadCount: counts.includedButBadCount + 1 };
      }
      return counts;
    },
    { excludedCount: 0, includedButBadCount: 0, redirectedCount: 0 }
  );
};

// Amazon single lookups
// Used from CommerceScraper's sequential batch-lookup loop (one lookup must complete before the
// next starts), so this needs the short visibility-wait bound (see
// SEQUENTIAL_VISIBILITY_WAIT_TIMEOUT_MS's comment) rather than the long default — including its
// single-lookup call sites, since the short bound is still a perfectly reasonable wait there too.
export const fetchAmazonProduct = async (
  asin: string,
  tld: string
): Promise<Record<string, unknown>> => {
  const res = await CommerceOpsLambdaFetch(`${SCRAPER_PREFIX}/amazon/product`, {
    method: 'POST',
    body: { asin, tld },
    visibilityWaitTimeoutMs: SEQUENTIAL_VISIBILITY_WAIT_TIMEOUT_MS
  });
  const data = await awaitJSON<Record<string, unknown>>(res);
  const status = classifyAmazonScrapeStatus(data);
  // 'redirected' carries real (if not this ASIN's own) product data — the backend intentionally
  // returns it rather than an empty scrape (see rainforestApiService.ts's redirect_asin handling),
  // so it should render like a successful lookup, not throw and hide it.
  if (status !== ScrapeStatus.OK && status !== ScrapeStatus.REDIRECTED) {
    throw new Error(`No product data returned for ASIN ${asin} (${status})`);
  }
  return data;
};

export const fetchAmazonSearch = async (
  query: string,
  tld: string,
  page: number,
  sort: string
): Promise<Record<string, unknown>> => {
  const res = await CommerceOpsLambdaFetch(`${SCRAPER_PREFIX}/amazon/search`, {
    method: 'POST',
    body: { query, tld, page, s: sort || undefined }
  });
  return awaitJSON(res);
};

export const fetchAmazonOffers = async (
  asin: string,
  tld: string
): Promise<Record<string, unknown>> => {
  const res = await CommerceOpsLambdaFetch(`${SCRAPER_PREFIX}/amazon/offers`, {
    method: 'POST',
    body: { asin, tld }
  });
  return awaitJSON(res);
};

// Used from CommerceScraper's sequential batch-lookup loop just like fetchAmazonProduct (see its
// comment) whenever the Reviews tab is active — same short visibility-wait bound applies.
export const fetchAmazonReviews = async (
  asin: string,
  tld: string
): Promise<Record<string, unknown>> => {
  const res = await CommerceOpsLambdaFetch(`${SCRAPER_PREFIX}/amazon/reviews`, {
    method: 'POST',
    body: { asin, tld },
    visibilityWaitTimeoutMs: SEQUENTIAL_VISIBILITY_WAIT_TIMEOUT_MS
  });
  return awaitJSON(res);
};

// Walmart single lookups — see fetchAmazonProduct's comment on the short visibility-wait bound.
export const fetchWalmartProduct = async (
  productId: string,
  tld: string
): Promise<Record<string, unknown>> => {
  const res = await CommerceOpsLambdaFetch(`${SCRAPER_PREFIX}/walmart/product`, {
    method: 'POST',
    body: { product_id: productId, tld },
    visibilityWaitTimeoutMs: SEQUENTIAL_VISIBILITY_WAIT_TIMEOUT_MS
  });
  return awaitJSON(res);
};

export const fetchWalmartSearch = async (
  query: string,
  tld: string,
  page: number
): Promise<Record<string, unknown>> => {
  const res = await CommerceOpsLambdaFetch(`${SCRAPER_PREFIX}/walmart/search`, {
    method: 'POST',
    body: { query, tld, page }
  });
  return awaitJSON(res);
};

export const fetchWalmartCategory = async (
  category: string,
  tld: string,
  page: number
): Promise<Record<string, unknown>> => {
  const res = await CommerceOpsLambdaFetch(`${SCRAPER_PREFIX}/walmart/category`, {
    method: 'POST',
    body: { category, tld, page }
  });
  return awaitJSON(res);
};

// Used from CommerceScraper's sequential batch-lookup loop just like fetchWalmartProduct (see
// fetchAmazonProduct's comment) whenever the Reviews tab is active — same short bound applies.
export const fetchWalmartReviews = async (
  productId: string,
  tld: string,
  sort: string,
  ratings?: number[],
  verifiedOnly?: boolean
): Promise<Record<string, unknown>> => {
  const res = await CommerceOpsLambdaFetch(`${SCRAPER_PREFIX}/walmart/reviews`, {
    method: 'POST',
    body: {
      product_id: productId,
      tld,
      sort: sort || undefined,
      ratings: ratings?.length ? ratings.join(',') : undefined,
      verified_purchase: verifiedOnly || undefined
    },
    visibilityWaitTimeoutMs: SEQUENTIAL_VISIBILITY_WAIT_TIMEOUT_MS
  });
  return awaitJSON(res);
};

// Submit once; Rainforest runs the whole batch server-side.
export const createAmazonProductCollection = async (
  asins: string[],
  tld: string
): Promise<{ collectionId: string }> => {
  const res = await CommerceOpsLambdaFetch(`${SCRAPER_PREFIX}/amazon/collection`, {
    method: 'POST',
    body: { asins, tld }
  });
  return awaitJSON(res);
};

export interface AmazonProductCollectionStatus {
  id: string;
  status: string;
  requestsTotal: number;
  resultSetIds: string[];
}

export const getAmazonProductCollectionStatus = async (
  collectionId: string
): Promise<AmazonProductCollectionStatus> => {
  const res = await CommerceOpsLambdaFetch(
    `${SCRAPER_PREFIX}/amazon/collection/${collectionId}/status`
  );
  return awaitJSON(res);
};

export const getAmazonProductCollectionResults = async (
  collectionId: string
): Promise<Record<string, unknown>[]> => {
  const res = await CommerceOpsLambdaFetch(
    `${SCRAPER_PREFIX}/amazon/collection/${collectionId}/results`
  );
  return awaitJSON(res);
};

export const parseIds = (text: string): string[] =>
  text
    .split(/[\n,]+/)
    .map(s => s.trim())
    .filter(Boolean);

export const isMultiInput = (text: string): boolean => parseIds(text).length > 1;

export const usesIdInput = (tab: ScraperTab): boolean => tab === 'product' || tab === 'reviews';

export const usesQueryInput = (tab: ScraperTab): boolean => tab === 'search';

export const usesCategoryInput = (tab: ScraperTab): boolean => tab === 'category';

export const getInputPlaceholder = (platform: Platform, tab: ScraperTab): string => {
  if (tab === 'search') {
    return 'Enter search query...';
  }
  if (tab === 'category') {
    return 'Enter Walmart category ID...';
  }
  if (platform === 'amazon') {
    return 'Enter ASINs (one per line for batch)...';
  }
  return 'Enter Walmart product IDs (one per line for batch)...';
};

export const getInputLabel = (platform: Platform, tab: ScraperTab): string => {
  if (tab === 'search') {
    return 'Search Query';
  }
  if (tab === 'category') {
    return 'Category ID';
  }
  if (platform === 'amazon') {
    return 'ASIN(s)';
  }
  return 'Product ID(s)';
};

const INVISIBLE_UNICODE_RE = /[\u200B-\u200F\u202A-\u202E\uFEFF]/g;

const stringifyValue = (value: unknown): string => {
  if (value == null) {
    return '';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value).replace(INVISIBLE_UNICODE_RE, '');
  }
  return String(value).replace(INVISIBLE_UNICODE_RE, '');
};

// Flatten nested API response for table display
export const flattenObject = (
  obj: Record<string, unknown>,
  prefix = ''
): Record<string, string> => {
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(obj)) {
    const fullKey = prefix ? `${prefix}.${key}` : key;
    if (Array.isArray(value)) {
      const hasObjects = value.some(v => v && typeof v === 'object');
      if (hasObjects) {
        value.forEach((item, i) => {
          if (typeof item === 'object' && item) {
            Object.assign(
              result,
              flattenObject(item as Record<string, unknown>, `${fullKey}.${i}`)
            );
          } else {
            result[`${fullKey}.${i}`] = stringifyValue(item);
          }
        });
      } else {
        result[fullKey] = value.map(stringifyValue).join(', ');
      }
    } else if (value && typeof value === 'object') {
      Object.assign(result, flattenObject(value as Record<string, unknown>, fullKey));
    } else {
      result[fullKey] = stringifyValue(value);
    }
  }
  return result;
};

// Mirrors the variant_name_* CSV columns in flattenAmazonProduct() below (and the backend's
// identical constant in scraperApiService.ts).
const KNOWN_VARIANT_DIMENSIONS = new Set([
  'size',
  'style',
  'color',
  'scent',
  'flavor',
  'item_package_quantity',
  'customer_package_type',
  'unit_count',
  'fit_type',
  'number_of_items',
  'team',
  'golf_club_flex',
  'configuration',
  'material_type',
  'item_display_weight',
  'pattern',
  'access_method',
  'edition',
  'platform_for_display',
  'special_size_type'
]);

// Mirrors the backend flattenProduct() — produces reference-column-named rows for CSV export
export const flattenAmazonProduct = (p: Record<string, unknown>): Record<string, unknown> => {
  const productInfo = p.product_information as Record<string, unknown> | undefined;
  const asin = String(p.asin ?? productInfo?.asin ?? '');
  const s = (v: unknown): string => (v == null ? '' : String(v));
  const clean = (v: unknown): string => s(v).replace(INVISIBLE_UNICODE_RE, '').trim();
  const coalesce = (...vals: unknown[]): unknown => {
    for (const v of vals) {
      if (v != null) return v;
    }
    return '';
  };

  const infoFields: Record<string, string> = {};
  if (productInfo && typeof productInfo === 'object') {
    const cleanInfo = (v: unknown): string =>
      String(v ?? '')
        .replace(INVISIBLE_UNICODE_RE, '')
        .trim();
    for (const [k, v] of Object.entries(productInfo)) {
      const key = `info_${k
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_]/g, '')}`;
      if (Array.isArray(v)) {
        infoFields[key] = (v as unknown[]).map(cleanInfo).join(' | ');
      } else if (typeof v === 'object') {
        infoFields[key] = JSON.stringify(v);
      } else {
        infoFields[key] = cleanInfo(v);
      }
    }
  }

  const rawBsr = coalesce(
    p.bestsellers_rank,
    p.best_sellers_rank,
    productInfo?.best_sellers_rank,
    productInfo?.bestsellers_rank
  );
  const bsrEntries: { rank: unknown; category: string; link: string }[] = [];
  if (Array.isArray(rawBsr)) {
    for (const entry of rawBsr as unknown[]) {
      if (typeof entry === 'string') {
        const stripped = entry.replace(/\s*\(See\b[^)]*\)\s*$/i, '').trim();
        const m = stripped.match(/^#([\d,]+)\s+in\s+(.+)$/);
        if (m) {
          bsrEntries.push({ rank: m[1], category: m[2].trim(), link: '' });
        } else if (stripped) {
          bsrEntries.push({ rank: '', category: stripped, link: '' });
        }
      } else if (entry && typeof entry === 'object') {
        const e = entry as Record<string, unknown>;
        const rank = s(e.rank ?? e.position ?? '');
        const category = s(e.category ?? e.name ?? e.label ?? '');
        if (rank || category) {
          bsrEntries.push({ rank, category, link: s(e.link ?? e.url ?? '') });
        }
      }
    }
  }
  const bsr = (i: number) => bsrEntries[i] ?? { rank: '', category: '', link: '' };
  const isTopRankedBsr = bsrEntries.some(e => String(e.rank).trim() === '1');

  const slugifyDim = (name: string): string =>
    name
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '');
  const variantAsins = new Set<string>();
  const currentVariantValues: Record<string, string> = {};
  const customOpts = p.customization_options as Record<string, unknown[]> | null | undefined;
  if (customOpts && typeof customOpts === 'object' && !Array.isArray(customOpts)) {
    for (const [dim, entries] of Object.entries(customOpts)) {
      const dimKey = slugifyDim(dim);
      if (!Array.isArray(entries)) continue;
      for (const e of entries as Record<string, unknown>[]) {
        if (e?.asin) {
          variantAsins.add(s(e.asin));
        }
        if (e?.is_selected && e?.value) {
          currentVariantValues[dimKey] = s(e.value);
        }
      }
    }
  }
  // Fallback: product_variants / variants arrays. Matches the backend's flattenProduct()
  // (DSH-32549, confirmed live): each entry is `{ asin, title, is_current_product, dimensions:
  // [{name, value}], ... }` — a variant can carry more than one dimension at once (e.g. size AND
  // color), and the current product's own entry is marked `is_current_product` in addition to
  // matching `asin`. The flat type/dimension/value/selected_value pair below is a secondary guess
  // kept in case some other vendor/shape reaches this function.
  if (!variantAsins.size) {
    let rawVariants: Record<string, unknown>[];
    if (Array.isArray(p.product_variants)) {
      rawVariants = p.product_variants as Record<string, unknown>[];
    } else if (Array.isArray(p.variants)) {
      rawVariants = p.variants as Record<string, unknown>[];
    } else {
      rawVariants = [];
    }
    for (const v of rawVariants) {
      if (!v || typeof v !== 'object') continue;
      if (v.asin) {
        variantAsins.add(s(v.asin));
      }
      const isCurrent = s(v.asin) === asin || v.is_current_product === true;
      if (!isCurrent) continue;
      if (Array.isArray(v.dimensions)) {
        for (const d of v.dimensions as Record<string, unknown>[]) {
          const dimKey = slugifyDim(s(d?.name));
          const dimVal = clean(d?.value);
          if (dimKey && dimVal) {
            currentVariantValues[dimKey] = dimVal;
          }
        }
      }
      const flatDimKey = slugifyDim(s(v.type ?? v.dimension ?? v.name ?? ''));
      const flatDimVal = s(v.value ?? v.selected_value ?? '');
      if (flatDimKey && flatDimVal && !currentVariantValues[flatDimKey]) {
        currentVariantValues[flatDimKey] = flatDimVal;
      }
    }
  }
  // Fallback: product_information. customization_options/product_variants only carry a value for
  // a dimension when the currently-scraped ASIN's own entry is present and marked selected/current
  // — some products (e.g. Milani Baked Blush ASINs) omit the current ASIN entirely, leaving every
  // variant_name_* column blank even though the product's own attribute value (e.g. color: "Dolce
  // Pink (Updated)") is present under product_information. Only fills dimensions the above didn't
  // already resolve, and only for known variant dimensions (product_information has many unrelated
  // keys).
  if (productInfo && typeof productInfo === 'object') {
    for (const [k, v] of Object.entries(productInfo)) {
      const dimKey = slugifyDim(k);
      if (KNOWN_VARIANT_DIMENSIONS.has(dimKey) && !currentVariantValues[dimKey]) {
        const dimVal = clean(v);
        if (dimVal) {
          currentVariantValues[dimKey] = dimVal;
        }
      }
    }
  }
  const varVal = (key: string) => currentVariantValues[key] ?? '';

  const newerModel = p.newer_model as Record<string, unknown> | undefined;

  const scrapeStatus = classifyAmazonScrapeStatus(p);

  return {
    asin,
    scrape_status: scrapeStatus,
    name: clean(p.name),
    brand: clean(p.brand) || clean(productInfo?.manufacturer ?? ''),
    brand_url: s(p.brand_url),
    price: s(p.pricing),
    buy_box_price_raw: s(p.pricing),
    list_price: s(p.list_price),
    shipping_price: s(p.shipping_price),
    shipping_time: s(p.shipping_time),
    availability: s(p.availability_status),
    rating: s(p.average_rating),
    total_reviews: s(p.total_reviews),
    total_ratings: s(p.total_ratings),
    pct_5_star: p['5_star_percentage'] ?? '',
    pct_4_star: p['4_star_percentage'] ?? '',
    pct_3_star: p['3_star_percentage'] ?? '',
    pct_2_star: p['2_star_percentage'] ?? '',
    pct_1_star: p['1_star_percentage'] ?? '',
    seller_name: s(p.seller_name),
    seller_id: s(p.seller_id),
    sold_by:
      typeof p.sold_by === 'string' ? p.sold_by : s((p.sold_by as Record<string, unknown>)?.name),
    sold_by_link: s(p.sold_by_link),
    seller_url: s(p.sold_by_link),
    ships_from:
      typeof p.ships_from === 'string'
        ? p.ships_from.split('\n')[0].trim()
        : s((p.ships_from as Record<string, unknown>)?.name),
    fulfilled_by_amazon: p.fulfilled_by_amazon ?? '',
    aplus_present: p.aplus_present ?? '',
    enhanced_content: coalesce(p.enhanced_content, p.aplus_present),
    is_coupon: p.is_coupon_exists ?? '',
    product_category: Array.isArray(p.product_category)
      ? (p.product_category as unknown[]).join(' > ')
      : s(p.product_category),
    feature_bullets: Array.isArray(p.feature_bullets)
      ? (p.feature_bullets as unknown[]).map(b => clean(b)).join(' | ')
      : clean(p.feature_bullets),
    feature_bullets_count: Array.isArray(p.feature_bullets) ? p.feature_bullets.length : '',
    full_description: clean(p.full_description),
    images: Array.isArray(p.images) ? [...new Set(p.images as string[])].join(' | ') : '',
    high_res_images: Array.isArray(p.high_res_images)
      ? (p.high_res_images as unknown[]).join(' | ')
      : '',
    count_of_images: Array.isArray(p.images) ? new Set(p.images as string[]).size : '',
    prime_flag: coalesce(p.is_prime, p.prime, p.prime_eligible, p.is_prime_eligible),
    amazons_choice_flag: p.is_amazons_choice ?? '',
    best_seller_flag: p.is_best_seller ?? (isTopRankedBsr ? 'Yes' : ''),
    // NOTE (DSH-32549, 2026-07): confirmed via live requests against ScraperAPI's product/
    // search/offers endpoints (two unrelated ASINs) that Climate Pledge Friendly is simply
    // absent from the response body. Rainforest API's docs claim a dedicated
    // `climate_pledge_friendly` field, but live testing (ASIN B0823J59FN — confirmed Climate
    // Pledge Friendly-certified via its own Amazon A+ content) found no such field there either:
    // the only trace was the phrase "Climate pledge friendly" inside an
    // a_plus_content.all_images[].name caption string. Neither vendor reliably exposes this as
    // clean data — every check below, including the A+ image-caption scan, is best-effort text
    // matching, not a real API contract. Expect false negatives.
    climate_pledge_friendly: (() => {
      if (p.climate_pledge_friendly != null) return p.climate_pledge_friendly;
      if (p.is_climate_pledge_friendly != null) return p.is_climate_pledge_friendly;
      const badgeLists = [
        p.badges,
        p.certifications,
        p.sustainability_features,
        p.important_information
      ];
      for (const list of badgeLists) {
        if (
          Array.isArray(list) &&
          (list as unknown[]).some(b => typeof b === 'string' && /climate.?pledge/i.test(b))
        ) {
          return true;
        }
      }
      if (productInfo && typeof productInfo === 'object') {
        const hit = Object.entries(productInfo).find(([k]) => /climate.?pledge/i.test(k));
        if (hit) return hit[1];
      }
      const aPlusImages = (p.a_plus_content as Record<string, unknown> | undefined)?.all_images;
      if (Array.isArray(aPlusImages)) {
        const hit = (aPlusImages as unknown[]).some(
          img =>
            typeof (img as Record<string, unknown>)?.name === 'string' &&
            /climate.?pledge/i.test((img as Record<string, unknown>).name as string)
        );
        if (hit) return true;
      }
      return '';
    })(),
    BusinessOnly: coalesce(p.is_business_only, p.business_only),
    // NOTE (DSH-32549, 2026-07): confirmed absent from ScraperAPI's responses. Rainforest API
    // DOES return this (confirmed live on 2/3 sample ASINs), nested as
    // subscribe_and_save.base_price.raw / .tiered_price.raw, not a flat `.price` — checked first
    // below, with the flatter shapes kept as fallbacks in case ScraperAPI or a future vendor
    // returns this differently.
    one_time_price: p.one_time_price ?? '',
    sns_price: (() => {
      if (p.sns_price != null) return p.sns_price;
      if (p.subscribe_price != null) return p.subscribe_price;
      const snsObj = p.subscribe_and_save;
      if (snsObj && typeof snsObj === 'object' && !Array.isArray(snsObj)) {
        const o = snsObj as Record<string, unknown>;
        const tieredPrice = o.tiered_price as Record<string, unknown> | undefined;
        const basePrice = o.base_price as Record<string, unknown> | undefined;
        return tieredPrice?.raw ?? basePrice?.raw ?? o.price ?? o.sns_price ?? '';
      }
      return '';
    })(),
    subscribe_and_save: (() => {
      if (p.is_subscribe_and_save != null) return p.is_subscribe_and_save;
      const snsObj = p.subscribe_and_save;
      if (snsObj && typeof snsObj === 'object' && !Array.isArray(snsObj)) {
        const o = snsObj as Record<string, unknown>;
        return (
          o.tiered_discount_percentage ??
          o.base_discount_percentage ??
          o.percentage ??
          o.percentage_saving ??
          o.savings_percentage ??
          true
        );
      }
      if (typeof p.subscribe_and_save === 'boolean') return p.subscribe_and_save;
      return '';
    })(),
    deal_price: p.deal_price ?? '',
    deal_type: p.deal_type ?? '',
    deal_type_percent_claimed: p.deal_type_percent_claimed ?? '',
    coupon_details: p.coupon_details ?? '',
    coupon_discount_percentage: coalesce(p.coupon_discount_percentage, p.coupon_percentage),
    online_price_currency: p.currency ?? '',
    // NOTE (DSH-32549, 2026-07): same vendor data gap as above — ScraperAPI never returns
    // video data for the ASINs sampled. Kept as a best-effort fallback only.
    video: (() => {
      const rawVideos = coalesce(p.videos, p.product_videos, p.video, p.has_video);
      if (Array.isArray(rawVideos)) {
        const urls = (rawVideos as unknown[])
          .map(v => {
            if (typeof v === 'string') return v;
            if (v && typeof v === 'object') {
              const r = v as Record<string, unknown>;
              return s(r.url ?? r.video_url ?? r.src ?? '');
            }
            return '';
          })
          .filter(Boolean);
        return urls.length ? urls.join(' | ') : '';
      }
      return rawVideos ?? '';
    })(),
    other_sellers: (() => {
      if (p.other_sellers_count != null) return p.other_sellers_count;
      if (p.num_other_sellers != null) return p.num_other_sellers;
      if (Array.isArray(p.other_sellers)) return p.other_sellers.length;
      if (typeof p.other_sellers === 'number') return p.other_sellers;
      return '';
    })(),
    fast_track_message: p.fast_track_message ?? '',
    URI: s(coalesce(p.product_url, p.url)) || `https://www.amazon.com/dp/${asin}`,
    asin_canonical: coalesce(p.canonical_asin, p.asin_canonical),
    redirect_asin: p.redirect_asin ?? '',
    pASIN: p.parent_asin ?? '',
    global_asin: p.global_asin ?? '',
    newer_version: (newerModel?.asin ?? p.newer_version_asin) ? 'YES' : '',
    newer_version_asin: s(newerModel?.asin ?? p.newer_version_asin),
    newer_version_title: s(newerModel?.title ?? p.newer_version_title),
    sales_ranks: bsrEntries.length ? JSON.stringify(bsrEntries) : '',
    count_of_bsr_assignments: bsrEntries.length || '',
    product_group: bsr(0).category,
    product_group_rank: bsr(0).rank,
    product_group_url: bsr(0).link,
    bsrank_0: bsr(0).rank,
    bscat_0: bsr(0).category,
    bsrank_1: bsr(1).rank,
    bscat_1: bsr(1).category,
    bsrank_2: bsr(2).rank,
    bscat_2: bsr(2).category,
    bsrank_3: bsr(3).rank,
    bscat_3: bsr(3).category,
    bsrank_4: bsr(4).rank,
    bscat_4: bsr(4).category,
    variant_asins: Array.from(variantAsins)
      .filter(a => a !== asin)
      .join(' | '),
    variation_type: Object.keys(currentVariantValues).join(' | '),
    variant_name_size: varVal('size'),
    variant_name_style: varVal('style'),
    variant_name_color: varVal('color'),
    variant_name_scent: varVal('scent'),
    variant_name_flavor: varVal('flavor'),
    variant_name_item_package_quantity: varVal('item_package_quantity'),
    variant_name_customer_package_type: varVal('customer_package_type'),
    variant_name_unit_count: varVal('unit_count'),
    variant_name_fit_type: varVal('fit_type'),
    variant_name_number_of_items: varVal('number_of_items'),
    variant_name_team: varVal('team'),
    variant_name_golf_club_flex: varVal('golf_club_flex'),
    variant_name_configuration: varVal('configuration'),
    variant_name_material_type: varVal('material_type'),
    variant_name_item_display_weight: varVal('item_display_weight'),
    variant_name_pattern: varVal('pattern'),
    variant_name_access_method: varVal('access_method'),
    variant_name_edition: varVal('edition'),
    variant_name_platform_for_display: varVal('platform_for_display'),
    variant_name_special_size_type: varVal('special_size_type'),
    ...infoFields
  };
};

export const downloadJson = (data: unknown, filename: string): void => {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

export const downloadCsvBlob = (csvContent: string, filename: string): void => {
  const blob = new Blob(['﻿', csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};
